import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
      if (menuOpen) setMenuOpen(false);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [menuOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    if (location.hash) {
      // Let the browser handle hash scrolling natively
      const el = document.querySelector(location.hash);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location]);

  const pageLink = ({ isActive }) =>
    `text-sm font-medium tracking-widest uppercase transition-colors duration-200 ${
      isActive ? 'text-blue-400' : 'text-slate-300 hover:text-white'
    }`;

  const anchorCls = 'text-sm font-medium tracking-widest uppercase transition-colors duration-200 text-slate-300 hover:text-white';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 h-16 transition-all duration-300 ${
        scrolled
          ? 'bg-[#060A0F]/90 backdrop-blur-md border-b border-white/[0.06]'
          : 'bg-transparent'
      }`}
    >
      {/* Inner wrapper — aligned with page content */}
      <div className="px-6 md:px-10 h-full">
      <div className="max-w-7xl mx-auto h-full flex items-center justify-between">

        {/* Logo */}
        <Link to="/" onClick={() => setMenuOpen(false)} className="flex items-center gap-3">
          <img src="/NN-logo-1.svg" alt="Nordic Numbers logo" className="h-8 w-auto" />
          <div className="flex flex-col leading-none">
            <span className="font-bold text-white tracking-tight text-base">Nordic</span>
            <span className="font-bold text-blue-400 tracking-tight text-base">Numbers</span>
          </div>
        </Link>

        {/* Desktop links — in chronological order of home page sections */}
        <div className="hidden md:flex items-center gap-8">
          <a href="/#expertise" className={anchorCls}>Expertise</a>
          <a href="/#epistemology" className={anchorCls}>Epistemology</a>
          <NavLink to="/company" className={pageLink}>Company</NavLink>
          <NavLink to="/causal-extractor" className={pageLink}>Causal Extractor</NavLink>
          <NavLink to="/contact" className={pageLink}>Contact</NavLink>
          <a
            href="https://www.linkedin.com/company/nordic-numbers/"
            target="_blank"
            rel="noopener noreferrer"
            className="opacity-60 hover:opacity-100 transition-opacity duration-200"
          >
            <img src="/basil_linkedin-outline.svg" alt="LinkedIn" className="h-5 w-5 invert" />
          </a>
        </div>

        {/* Hamburger button */}
        <button
          className="md:hidden flex flex-col gap-[5px] p-2"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`block h-px w-6 bg-white transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-[7px]' : ''}`} />
          <span className={`block h-px w-6 bg-white transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} />
          <span className={`block h-px w-6 bg-white transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`} />
        </button>

      </div>
      </div>

      {/* Mobile menu — full width overlay */}
      <div
        ref={menuRef}
        className={`absolute top-16 left-0 right-0 bg-[#060A0F]/95 backdrop-blur-md border-b border-white/[0.06] flex flex-col items-center gap-6 py-8 md:hidden transition-all duration-300 ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none -translate-y-2'
        }`}
      >
        <a href="/#expertise" className={anchorCls} onClick={() => setMenuOpen(false)}>Expertise</a>
        <a href="/#epistemology" className={anchorCls} onClick={() => setMenuOpen(false)}>Epistemology</a>
        <NavLink to="/company" className={pageLink} onClick={() => setMenuOpen(false)}>Company</NavLink>
        <NavLink to="/causal-extractor" className={pageLink} onClick={() => setMenuOpen(false)}>Causal Extractor</NavLink>
        <NavLink to="/contact" className={pageLink} onClick={() => setMenuOpen(false)}>Contact</NavLink>
        <a
          href="https://www.linkedin.com/company/nordic-numbers/"
          target="_blank"
          rel="noopener noreferrer"
          className="opacity-60 hover:opacity-100 transition-opacity duration-200"
        >
          <img src="/basil_linkedin-outline.svg" alt="LinkedIn" className="h-5 w-5 invert" />
        </a>
      </div>
    </nav>
  );
};

export default Navbar;
